<template>
  <div class="box">
    <b-loading
      v-if="isLoading"
      :is-full-page="false"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h2 class="has-text-weight-bold">Editar Informações Principais</h2>
    <br />

    <div class="columns">
      <!-- Logo OE -->
      <div class="column is-narrow">
        <div
          class="logo-upload-div"
          :style="{ 'background-image': `url(${previewImage})` }"
          @click="selectImage"
        >
          <b-icon
            icon="camera-retro"
            size="is-large"
            style="position: relative; left: 23px; top: 12px; color: #999"
            v-if="!previewImage"
          ></b-icon>

          <p
            class="has-text-centered"
            style="font-size: 0.8rem; position: relative; top: 6px; color: #999"
            v-if="!previewImage"
          >
            Logo PNG
          </p>

          <div class="logoEditing">
            <b-icon icon="pencil-alt" size="is-small"></b-icon>
          </div>
        </div>

        <p
          class="has-text-centered delete-organization-logo"
          @click="deleteOrganizationLogo"
        >
          <u>Excluir logo</u>
        </p>

        <input
          ref="fileInput"
          type="file"
          @input="pickFile"
          style="display: none"
          accept="image/png"
        />
      </div>

      <!-- A/O + Nome da OE -->
      <div class="column">
        <b-tooltip
          label="Para alterar o nome da sua OE, entre em contato com a Empower"
          expanded
          position="is-bottom"
        >
          <b-field style="width: 100%;">
            <b-field
              :label="
                oe.treatmentPronoun ? 'Pronome de tratamento / Nome da OE' : ''
              "
              label-position="on-border"
            >
              <b-select
                v-model="oe.treatmentPronoun"
                placeholder="Pronome"
                disabled
              >
                <option value="">Pronome</option>
                <option value="a">a</option>
                <option value="o">o</option>
              </b-select>

              <b-input
                v-model="oe.name"
                placeholder="Nome da sua OE..."
                expanded
                disabled
              ></b-input>
            </b-field>
          </b-field>
        </b-tooltip>

        <!-- Categoria -->
        <b-field
          :label="oe.category ? 'Categoria' : ''"
          label-position="on-border"
          style="margin-top: 1.5rem"
        >
          <b-select v-model="oe.category" expanded>
            <option value="">Categoria</option>
            <option value="Aerodesign">Aerodesign</option>
            <option value="AIESEC">AIESEC</option>
            <option value="Atlética Universitária">
              Atlética Universitária
            </option>
            <option value="Baja">Baja</option>
            <option value="Barco Solar">Barco Solar</option>
            <option value="Centro Acadêmico">Centro Acadêmico</option>
            <option value="Clube de Consultoria">Clube de Consultoria</option>
            <option value="Clube de Empreendedorismo">
              Clube de Empreendedorismo
            </option>
            <option value="Coletivo">Coletivo</option>
            <option value="Cursos Populares">Cursos Populares</option>
            <option value="Diretório Acadêmico">Diretório Acadêmico</option>
            <option value="Eficiência Energética">Eficiência Energética</option>
            <option value="Empresa Júnior">Empresa Júnior</option>
            <option value="Enactus">Enactus</option>
            <option value="Engenheiros sem Fronteiras">
              Engenheiros sem Fronteiras
            </option>
            <option value="Equipe de Foguetes">Equipe de Foguetes</option>
            <option value="Evento">Evento</option>
            <option value="Feira de Carreiras">Feira de Carreiras</option>
            <option value="Fórmula">Fórmula</option>
            <option value="Grupo de Estudos">Grupo de Estudos</option>
            <option value="Grupo de Extensão">Grupo de Extensão</option>
            <option value="Grupo de Pesquisa">Grupo de Pesquisa</option>
            <option value="IEEE">IEEE</option>
            <option value="Liga Acadêmica">Liga Acadêmica</option>
            <option value="Liga de Mercado Financeiro">
              Liga de Mercado Financeiro
            </option>
            <option value="ONG">ONG</option>
            <option value="PET (Programa de Educação Tutorial)">
              PET (Programa de Educação Tutorial)
            </option>
            <option value="Robótica">Robótica</option>
            <option value="Semana de Curso">Semana de Curso</option>
            <option value="separador" disabled>-------------</option>
            <option value="Outra">Outros Grupos</option>
          </b-select>
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <!-- E-mail institucional -->
        <b-field
          :label="oe.organizationEmail ? 'E-mail institucional' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="oe.organizationEmail"
            placeholder="E-mail institucional"
            expanded
          >
          </b-input>
        </b-field>
      </div>

      <div class="column">
        <!-- Estado -->
        <b-field :label="oe.state ? 'UF' : ''" label-position="on-border">
          <b-autocomplete
            v-model="oe.state"
            :data="filteredStatesArray"
            :open-on-focus="true"
            :keep-first="true"
            placeholder="UF"
            @select="option => (selected = option)"
          >
            <template slot="empty"> Sem resultados </template>
          </b-autocomplete>
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <!-- Cidade -->
        <b-field :label="oe.city ? 'Cidade' : ''" label-position="on-border">
          <b-autocomplete
            v-model="oe.city"
            :data="filteredCitiesArray"
            :open-on-focus="true"
            :keep-first="true"
            placeholder="Cidade"
            @select="option => (selected = option)"
            :disabled="!isValidState"
          >
            <template slot="empty"> Sem resultados </template>
          </b-autocomplete>
        </b-field>
      </div>

      <div class="column">
        <!-- OE - Universidade -->
        <b-field
          :label="oe.university ? 'Universidade' : ''"
          label-position="on-border"
        >
          <b-autocomplete
            v-model="oe.university"
            :data="filteredUniversitiesArray"
            :open-on-focus="true"
            :keep-first="true"
            placeholder="Universidade"
            @select="option => (selected = option)"
            :disabled="!isValidState"
          >
            <template slot="empty"> Sem resultados </template>
          </b-autocomplete>
        </b-field>
      </div>
    </div>

    <div class="columns" v-show="isAnotherUniversity">
      <div class="column">
        <!-- Qual outra universidade? -->
        <b-field
          :label="oe.anotherUniversity ? 'Qual outra Universidade?' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="oe.anotherUniversity"
            placeholder="Qual outra Universidade?"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns" v-show="isVariousUniversities">
      <div class="column">
        <!-- Quais outras universidades? -->
        <b-field
          :label="oe.variousUniversities ? 'Quais outras Universidades?' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="oe.variousUniversities"
            placeholder="Quais outras Universidades?"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <!-- OE - Facebook -->
        <b-field
          :label="oe.fanpage ? 'fanpage da OE' : ''"
          label-position="on-border"
        >
          <p class="control">
            <span class="button is-static">facebook.com/</span>
          </p>

          <b-input
            v-model="linkFace"
            placeholder="fanpage da OE"
            expanded
          ></b-input>

          <b-tooltip 
            label="Clique para saber se esse link está te redirecionando para a página correta do Facebook" 
            position="is-left"
            style="width: fit-content;"
            v-if="windowWidth > 400"
          >
            <a :href="'https://facebook.com/' + linkFace" target="_blank">
              <b-button>
                <b-icon icon="eye" size="is-medium"></b-icon>
              </b-button>
            </a>
          </b-tooltip>
        </b-field>

        <b-tooltip
          label="Clique para saber se esse link está te redirecionando para a página correta do Facebook"
          position="is-top"
          v-if="windowWidth <= 400"
        >
          <a
            :href="'https://facebook.com/' + linkFace"
            target="_blank"
            style="width: 100%;"
          >
            <b-button expanded>
              <b-icon icon="eye" size="is-medium"></b-icon>
            </b-button>
          </a>
        </b-tooltip>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <!-- OE - Insta -->
        <b-field
          :label="oe.insta ? 'insta da OE' : ''"
          label-position="on-border"
        >
          <p class="control">
            <span class="button is-static">instagram.com/</span>
          </p>
          <b-input
            v-model="linkInsta"
            placeholder="insta da OE"
            expanded
          ></b-input>

          <b-tooltip
            label="Clique para saber se esse link está te redirecionando para a página correta do Instagram"
            position="is-left"
            style="width: fit-content;"
            v-if="windowWidth > 400"
          >
            <a :href="'https://instagram.com/' + linkInsta" target="_blank">
              <b-button>
                <b-icon icon="eye" size="is-medium"></b-icon>
              </b-button>
            </a>
          </b-tooltip>
        </b-field>

        <b-tooltip
          label="Clique para saber se esse link está te redirecionando para a página correta do Instagram"
          position="is-top"
          v-if="windowWidth <= 400"
        >
          <a
            :href="'https://instagram.com/' + linkInsta"
            target="_blank"
            style="width: 100%;"
          >
            <b-button expanded>
              <b-icon icon="eye" size="is-medium"></b-icon>
            </b-button>
          </a>
        </b-tooltip>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <!-- OE - LinkedIn -->
        <b-field
          :label="oe.linkedin ? 'linkedin da OE' : ''"
          label-position="on-border"
        >
          <p class="control">
            <span class="button is-static">linkedin.com/company/</span>
          </p>
          <b-input
            v-model="linkLinkedin"
            placeholder="linkedin da OE"
            expanded
          ></b-input>
          <b-tooltip
            label="Clique para saber se esse link está te redirecionando para a página correta do Linkedin"
            position="is-left"
            style="width: fit-content;"
            v-if="windowWidth > 400"
          >
            <a
              :href="'https://linkedin.com/company/' + linkLinkedin"
              target="_blank"
            >
              <b-button>
                <b-icon icon="eye" size="is-medium"></b-icon>
              </b-button>
            </a>
          </b-tooltip>
        </b-field>

        <b-tooltip
          label="Clique para saber se esse link está te redirecionando para a página correta do Linkedin"
          position="is-top"
          v-if="windowWidth <= 400"
        >
          <a
            :href="'https://linkedin.com/company' + linkLinkedin"
            target="_blank"
            style="width: 100%;"
          >
            <b-button expanded>
              <b-icon icon="eye" size="is-medium"></b-icon>
            </b-button>
          </a>
        </b-tooltip>
      </div>
    </div>
    <Pendencies :pendencies="pendencies" />
    <hr />
    <div>
      <b-button
        class="button is-danger is-rounded is-pulled-left"
        @click="confirmOrganizationDeletion"
        outlined
      >
        Excluir conta
      </b-button>

      <span class="is-pulled-left">
        &nbsp;&nbsp;&nbsp;
      </span>

      <button
        class="button is-primary is-rounded"
        style="margin-left: auto; display: block"
        @click="updateOrganization"
        :disabled="pendencies.length > 0"
      >
        Salvar
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { commom } from "../../../mixins/commom";
import { cidades_por_estado } from "../../../assets/js/cidades-por-estado";
import { estados } from "../../../assets/js/estados";
import { universidades_por_estado } from "../../../assets/js/universidades-por-estado";
import { responsive } from "../../../mixins/responsive";
import Pendencies from "../../commom/Pendencies.vue";

export default {
  name: "FormMainInfo",
  props: ["oe"],
  mixins: [responsive, commom],
  components: {
    Pendencies
  },
  data() {
    return {
      isLoading: false,
      citiesList: {},
      universitiesList: {},
      statesList: [],
      previewImage: null,
      linkFace: "",
      linkInsta: "",
      linkLinkedin: "",
      file: null
    };
  },

  methods: {
    selectImage() {
      this.$refs.fileInput.click();
    },

    pickFile() {
      let input = this.$refs.fileInput;
      let file = input.files;
      if (file && file[0]) {
        let reader = new FileReader();
        reader.onload = e => {
          this.previewImage = e.target.result;
        };
        reader.readAsDataURL(file[0]);
        this.$emit("input", file[0]);
        this.file = file[0];
      }
    },

    updateOrganization() {
      this.isLoading = true;
      if (this.linkFace != "") {
        this.oe.fanpage = "https://facebook.com/" + this.linkFace;
      } else {
        this.oe.fanpage = "";
      }
      if (this.linkInsta != "") {
        this.oe.insta = "https://instagram.com/" + this.linkInsta;
      } else {
        this.oe.insta = "";
      }
      if (this.linkLinkedin != "") {
        this.oe.linkedin = "https://linkedin.com/company/" + this.linkLinkedin;
      } else {
        this.oe.linkedin = "";
      }

      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("oe", JSON.stringify(this.oe));

      this.$store
        .dispatch("updateOrganization", formData)
        .then(res => {
          if (res.data.result === "ok") {
            this.showOrganizationEmailInput = false;
            this.$buefy.dialog.alert({
              title: "Sucesso!",
              message: "As informações principais da OE foram atualizadas 😉",
              type: "is-success",
              hasIcon: true,
              icon: "check",
              iconPack: "fa"
            });
            if (this.oe.fanpage) {
              this.oeHasFanpage = true;
              if (this.oe.fanpage.indexOf("https") === -1) {
                this.oe.fanpage = "https://www.facebook.com/" + this.oe.fanpage;
              }
            }
            if (this.oe.insta) {
              this.oeHasInsta = true;
              if (this.oe.insta.indexOf("https") === -1) {
                this.oe.insta = "https://www.instagram.com/" + this.oe.insta;
              }
            }
            if (this.oe.linkedin) {
              this.oeHasLinkedin = true;
              if (this.oe.linkedin.indexOf("https") === -1) {
                this.oe.linkedin =
                  "https://www.linkedin.com/company/" + this.oe.linkedin;
              }
            }
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    linkStructure() {
      if (this.oe.fanpage) {
        this.linkFace = this.oe.fanpage.split("/")[3];
      }
      if (this.oe.insta) {
        this.linkInsta = this.oe.insta.split("/")[3];
      }
      if (this.oe.linkedin) {
        this.linkLinkedin = this.oe.linkedin.split("/")[4];
      }
    },

    deleteOrganizationLogo() {
      if (!this.previewImage) {
        this.$buefy.toast.open({
          message: "Não existe imagem",
          type: "is-warning"
        });
        return false;
      }
      this.isLoading = true;
      this.$store
        .dispatch("deleteOrganizationLogo")
        .then(res => {
          if (res.data.result === "ok") {
            this.previewImage = null;
            this.$buefy.toast.open({
              message: "Imagem deletada",
              type: "is-success"
            });
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmOrganizationDeletion() {
      this.$buefy.dialog.confirm({
        message:
          "A Empower será notificada da sua solicitação de exclusão de conta e em até 3 dias úteis efetivará \
          a exclusão.\r\nCaso você tenha contrapartidas ou ações pendentes a conta da OE não será excluída.",
        type: "is-danger",
        cancelText: "Cancelar",
        confirmText: "Sim, excluir",
        onConfirm: () => {
          this.isLoading = true;
          this.$store
            .dispatch("deleteOrganization")
            .then(res => {
              if (res.data.result === "ok") {
                this.previewImage = null;
                this.$buefy.toast.open({
                  message: "Solicitação enviada!",
                  type: "is-success"
                });
              } else {
                this.alertError(res.data);
              }
            })
            .catch(() => {
              this.alertError({ message: "UNEXPECTED_ERROR" });
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      });
    }
  },

  computed: {
    ...mapGetters(["account"]),

    isAnotherUniversity() {
      return this.oe.university === "Outra";
    },

    isVariousUniversities() {
      return this.oe.university === "Várias";
    },

    filteredStatesArray() {
      return this.statesList.filter(option => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.oe.state.toLowerCase()) >= 0
        );
      });
    },

    filteredCitiesArray() {
      let filteredCitiesArray = [];
      if (this.isValidState) {
        filteredCitiesArray = this.citiesList[this.oe.state].filter(option => {
          return (
            option
              .toString()
              .toLowerCase()
              .indexOf(this.oe.city.toLowerCase()) >= 0
          );
        });
      }
      return filteredCitiesArray;
    },
    filteredUniversitiesArray() {
      let filteredUniversitiesArray = [];
      if (this.isValidState) {
        filteredUniversitiesArray = this.universitiesList[this.oe.state].filter(
          option => {
            return (
              option
                .toString()
                .toLowerCase()
                .indexOf(this.oe.university.toLowerCase()) >= 0
            );
          }
        );
      }
      return filteredUniversitiesArray;
    },

    isValidState() {
      return this.statesList.includes(this.oe.state);
    },

    pendencies() {
      const pendencies = [];

      if (this.oe.organizationEmail == "") {
        pendencies.push("E-mail institucional");
      }

      if (this.oe.state == "") {
        pendencies.push("UF (Estado)");
      }

      if (this.oe.city == "") {
        pendencies.push("Cidade");
      }

      if (this.oe.university == "") {
        pendencies.push("Universidade");
      }

      if (this.linkFace == "") {
        if (this.linkInsta == "") {
          if (this.linkLinkedin == "") {
            pendencies.push(
              "É necessário possuir ao menos uma rede social (Facebook, Instagram ou Linkedin)"
            );
          }
        }
      }

      return pendencies;
    }
  },

  mounted() {
    this.previewImage = this.oe.logo;
    this.isLoading = false;
    this.citiesList = cidades_por_estado;
    this.universitiesList = universidades_por_estado;
    this.statesList = estados;
    this.linkStructure();
  }
};
</script>

<style scoped>
.box {
  padding: 2rem 3rem;
  max-width: 800px !important;
  margin: auto;
}
.logo-upload-div {
  width: 96px;
  height: 96px;
  border: 1px solid #999;
  color: #999;
  border-radius: 50%;
  cursor: pointer;
  background-size: cover;
  background-position: center center;
}
.logo-upload-div:hover {
  opacity: 0.5;
}

.delete-organization-logo {
  font-size: 80%;
  color: red;
  cursor: pointer;
}

.b-tooltip {
  width: 100%;
}
.logoEditing {
  border: solid gray 0.5px;
  border-radius: 100%;
  background-color: white;
  width: 1.8rem;
  height: 1.8rem;
  margin-left: auto;
  margin-bottom: -10rem;
}
.logoEditing .icon {
  display: block;
  margin-left: auto;
  margin-right: auto;
  color: gray;
}
.logoEditing:hover {
  opacity: 1;
  transform: scale(1.5);
}

@media (max-width: 400px) {
  .box {
    padding: 0.5rem !important;
  }
}
</style>
